// src/context/BillingContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { billingService } from '../api/billingService'; // Ensure this path is correct
import { useAuth } from './AuthContext'; // To trigger fetch on auth change

const BillingContext = createContext(undefined);

export const BillingProvider = ({ children }) => {
  const { user, initialAuthChecked, loading: authLoading } = useAuth(); // Added authLoading
  const [accountStatus, setAccountStatus] = useState(null); // Holds UserCreditsAndSubscriptionResponse
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);

  const fetchAccountStatusData = useCallback(async (showLoadingIndicator = true) => {
    // Wait for initial auth check to complete AND user to be definitively known (or null)
    if (authLoading || !initialAuthChecked) {
      // console.log("BillingContext: Auth not ready, skipping fetch.");
      if (showLoadingIndicator) setIsLoadingStatus(true); // Keep loading if auth is not settled
      setAccountStatus(null); // Ensure no stale data if user logs out
      return;
    }

    if (!user) {
      // console.log("BillingContext: No user, clearing account status.");
      setAccountStatus(null);
      setStatusError(null);
      setIsLoadingStatus(false);
      return;
    }

    // console.log("BillingContext: Auth ready and user exists, fetching account status...");
    if (showLoadingIndicator) setIsLoadingStatus(true);
    setStatusError(null);
    const res = await billingService.getAccountStatus();

    if (res.isError) {
      // console.error("BillingContext: Error fetching account status:", res.message);
      setStatusError(res.message);
      setAccountStatus(null); // Clear on error to avoid showing stale/incorrect data
    } else {
      // console.log("BillingContext: Account status fetched successfully:", res);
      setAccountStatus(res);
    }
    setIsLoadingStatus(false);
  }, [user, initialAuthChecked, authLoading]);

  useEffect(() => {
    // Fetch on mount and when user/auth state changes
    // The useCallback for fetchAccountStatusData includes dependencies on user and initialAuthChecked
    fetchAccountStatusData();
  }, [fetchAccountStatusData]); // fetchAccountStatusData is memoized with its own deps

  const value = {
    accountStatus,
    isLoadingStatus,
    statusError,
    refreshAccountStatus: () => fetchAccountStatusData(false), // Allow refresh without strong loading indicator
    forceRefreshAccountStatus: () => fetchAccountStatusData(true), // Allow refresh WITH strong loading indicator
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};